import {
  Box,
  Button,
  FormControl,
  FormLabel,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Typography,
} from '@mui/material';
import classNames from 'classnames/bind';
import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { AccountCircleLine, HidePassword, Key2FillIcon, VectorIcon } from '~/assets/icons';
import { BASE_COLORS } from '~/utils/constants/common';
import { adminRoute, adminRouteAbsolute } from '~/utils/constants/route';
import { handleApiError } from '~/utils/helper';
import { ILoginPayload } from '~/utils/interface/auth';
import styles from '../auth.module.scss';
import AuthLayout from '../AuthLayout';
import { useAppDispatch } from '~/redux/hooks';
import { handleLogin } from '~/thunks/auth/authThunk';
import { useFormik } from 'formik';
const cx = classNames.bind(styles);

const initialValues: ILoginPayload = {
  username: '',
  password: '',
};
const LoginAdmin = () => {
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const dispatch = useAppDispatch();

  const navigate = useNavigate();
  const { t } = useTranslation(['admin/auth']);

  const form = useFormik<ILoginPayload>({
    initialValues,
    onSubmit: async (values, { setFieldError }) => {
      try {
        await dispatch(handleLogin(values)).unwrap();
        navigate(`${adminRouteAbsolute.dashboard}`);
      } catch (error) {
        handleApiError(error);
      }
    },
  });

  return (
    <AuthLayout>
      <Typography className='text-center font-bold text-[#ED1002] text-[24px]' sx={{ mt: 4, mb: 6 }}>{`${t(
        'login.login'
      ).toUpperCase()}`}</Typography>
      <Box>
        <Box className={cx('input-custom')}>
          <Box>
            <FormControl fullWidth>
              <FormLabel htmlFor='username' className='mb-2 font-semibold text-black text-[13px]'>
                {`${t('login.username')}`}
              </FormLabel>
              <OutlinedInput
                id='username'
                type='text'
                placeholder={t('login.usernamePlaceholder')}
                name='username'
                sx={{
                  '& .MuiOutlinedInput-input': {
                    padding: '0 14px',
                    height: '100%',
                    boxSizing: 'border-box',
                  },
                }}
                value={form.values.username}
                onChange={form.handleChange}
                startAdornment={
                  <InputAdornment position='start'>
                    <AccountCircleLine fill={BASE_COLORS.BLUE_BOLD} />
                  </InputAdornment>
                }
              />
            </FormControl>
          </Box>
          <Box className='mt-5'>
            <FormControl fullWidth>
              <FormLabel htmlFor='password' className='mb-2 font-semibold text-black text-[13px]'>
                {`${t('login.password')}`}
              </FormLabel>
              <OutlinedInput
                id='password'
                type={showPassword ? 'text' : 'password'}
                placeholder={t('login.passwordPlaceholder')}
                name='password'
                value={form.values.password}
                onChange={form.handleChange}
                sx={{
                  '& .MuiOutlinedInput-input': {
                    padding: '0 14px',
                    height: '100%',
                    boxSizing: 'border-box',
                  },
                }}
                endAdornment={
                  <InputAdornment position='end'>
                    <IconButton
                      aria-label={showPassword ? 'hide the password' : 'display the password'}
                      onClick={() => {
                        setShowPassword(!showPassword);
                      }}
                      edge='end'
                    >
                      {showPassword ? (
                        <VectorIcon fill={BASE_COLORS.BLUE_BOLD} />
                      ) : (
                        <HidePassword fill={BASE_COLORS.BLUE_BOLD} />
                      )}
                    </IconButton>
                  </InputAdornment>
                }
                startAdornment={
                  <InputAdornment position='start'>
                    <Key2FillIcon />
                  </InputAdornment>
                }
              />
            </FormControl>
          </Box>
        </Box>
        <Box className='text-right mt-3'>
          <Link
            to={`${adminRoute.base}${adminRoute.forgotPassword}`}
            className='underline text-[12.64px] font-semibold text-[#72777B]'
          >
            {`${t('login.forgotPassword')}`}
          </Link>
        </Box>
        <Button
          className='bg-[#ce0209] text-white w-full mt-3 text-[15px] rounded-[12px]'
          sx={{
            backgroundColor: 'blue',
            color: 'white',
            '&:hover': {
              backgroundColor: '#F44336',
            },
          }}
          onClick={() => form.handleSubmit()}
        >
          {`${t('login.login')}`}
        </Button>
      </Box>
    </AuthLayout>
  );
};
export default memo(LoginAdmin);
